import { useState } from "react";
import AddressAutocomplete from "./AddressAutocomplete";
import MapPicker from "./MapPicker";
import { Link } from "react-router-dom";
import DeleteAlert from "./DeleteAlert";
import { useLanguage } from "../contexts/LanguageContext";
import { TrashIcon, PencilIcon } from "@heroicons/react/24/outline";

export default function BranchList({
  branches,
  onAddBranch,
  onEditBranch,
  onDeleteBranch,
  isAdding,
  onCancelAdd,
  store,
}) {
  const { t } = useLanguage();
  const [editingBranchId, setEditingBranchId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingLocation, setEditingLocation] = useState(null);
  const [newBranchName, setNewBranchName] = useState("");
  const [newBranchLocation, setNewBranchLocation] = useState(null);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [deleteBranchId, setDeleteBranchId] = useState(null);
  // Remounts the Maps-backed inputs when the merchant retries a failed load.
  // AddressAutocomplete owns the retry button; MapPicker hid itself on the same
  // shared promise and has no way to notice a retry on its own.
  const [mapsAttempt, setMapsAttempt] = useState(0);
  const retryMaps = () => setMapsAttempt((n) => n + 1);

  const handleAddBranch = (location) => {
    if (location) {
      setNewBranchLocation(location);
    }
  };

  const handleSaveBranch = () => {
    if (!newBranchName.trim()) {
      alert(t("settings.branches.form.nameRequired"));
      return;
    }
    if (!newBranchLocation) {
      alert(t("settings.branches.form.addressRequired"));
      return;
    }

    onAddBranch({
      name: newBranchName,
      address: newBranchLocation.address,
      latitude: newBranchLocation.lat,
      longitude: newBranchLocation.lng,
    });
    onCancelAdd();
    setNewBranchName("");
    setNewBranchLocation(null);
  };

  const handleEditBranch = (branch) => {
    if (!editingName.trim()) {
      alert(t("settings.branches.form.nameRequired"));
      return;
    }

    onEditBranch(branch.id, {
      ...branch,
      name: editingName,
      address: editingLocation?.address || branch.address,
      latitude: editingLocation?.lat || branch.latitude,
      longitude: editingLocation?.lng || branch.longitude,
    });
    setEditingBranchId(null);
    setEditingName("");
    setEditingLocation(null);
  };

  const startEditing = (branch) => {
    setEditingBranchId(branch.id);
    setEditingName(branch.name);
    setEditingLocation(null);
  };

  return (
    <div className="space-y-4">
      {isAdding && (
        <div className="p-6 border rounded-lg bg-gray-50 space-y-4">
          <h4 className="text-sm font-medium text-gray-900">
            {t("settings.branches.form.newBranch")}
          </h4>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("settings.branches.form.name")}
            </label>
            <input
              type="text"
              value={newBranchName}
              onChange={(e) => setNewBranchName(e.target.value)}
              placeholder={t("settings.branches.form.namePlaceholder")}
              className="block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("settings.branches.form.address")}
            </label>
            <AddressAutocomplete
              key={`address-${mapsAttempt}`}
              onSelectAddress={handleAddBranch}
              onRetry={retryMaps}
            />
            <div className="mt-3">
              <MapPicker
                key={`map-${mapsAttempt}`}
                location={newBranchLocation}
                onSelectLocation={handleAddBranch}
              />
            </div>
          </div>
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={() => {
                onCancelAdd();
                setNewBranchName("");
                setNewBranchLocation(null);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={handleSaveBranch}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
            >
              {t("common.save")}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {branches.map((branch) => (
          <div
            key={branch.id}
            className="bg-white border rounded-lg shadow-xs hover:shadow-sm transition-shadow duration-200"
          >
            <div className="p-6">
              {editingBranchId === branch.id ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("settings.branches.form.name")}
                    </label>
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("settings.branches.form.address")}
                    </label>
                    <AddressAutocomplete
                      key={`address-${mapsAttempt}`}
                      initialValue={branch.address}
                      onSelectAddress={(location) =>
                        setEditingLocation(location)
                      }
                      onRetry={retryMaps}
                    />
                    <div className="mt-3">
                      <MapPicker
                        key={`map-${mapsAttempt}`}
                        location={
                          editingLocation || {
                            address: branch.address,
                            lat: branch.latitude,
                            lng: branch.longitude,
                          }
                        }
                        onSelectLocation={setEditingLocation}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3 mt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingBranchId(null);
                        setEditingName("");
                        setEditingLocation(null);
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500"
                    >
                      {t("common.cancel")}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleEditBranch(branch)}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                    >
                      {t("common.save")}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <h3 className="text-sm font-medium text-gray-900">
                      {branch.name}
                    </h3>
                    <p className="text-sm text-gray-500">{branch.address}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => startEditing(branch)}
                      className="text-sm text-indigo-600"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDeleteBranchId(branch.id);
                        setIsDeleteAlertOpen(true);
                      }}
                      className="text-sm text-red-600"
                    >
                      <TrashIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <DeleteAlert
        isOpen={isDeleteAlertOpen}
        onClose={() => setIsDeleteAlertOpen(false)}
        onConfirm={() => {
          onDeleteBranch(deleteBranchId);
          setIsDeleteAlertOpen(false);
        }}
      />
    </div>
  );
}
